import { useCallback, useMemo, useState } from 'react';
import { PaymentContext, PaymentContextValues } from '@hooks/usePayment';
import { fetchStripeCheckoutSecret } from '@modules/api';
import { Dialog, DialogTitle } from '@mui/material';
import stripePromise from '@utils/stripe-client';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ResultAction, StripeEmbeddedCheckoutShippingDetailsChangeEvent } from '@stripe/stripe-js';
import Stripe from 'stripe';
import { breadcrumb, log, logError } from '@utils/logging';
import { stringFromError } from '@utils/utils';

export default function PaymentProvider({
    children,
}: {
    children: React.JSX.Element;
}): React.JSX.Element {
    const [showModal, setShowModal] = useState(false);
    const [checkoutSecret, setCheckoutSecret] = useState<
        Stripe.Response<Stripe.Checkout.Session> | undefined
    >(undefined);

    const checkoutOptions: {
        clientSecret?: string | null;
        fetchClientSecret?: (() => Promise<string>) | null;
        onComplete?: () => void;
        onShippingDetailsChange?: (
            event: StripeEmbeddedCheckoutShippingDetailsChangeEvent,
        ) => Promise<ResultAction>;
    } = {
        clientSecret: checkoutSecret?.client_secret,
        // NOTE: we could actually handle many payments with this function instead of webhooks:
        // https://docs.stripe.com/payments/checkout/custom-success-page?payment-ui=embedded-form#redirect-if-required
        onComplete: () => {
            console.log('Payment completed');
            setShowModal(false);
        },
    };

    const processEventPayment: PaymentContextValues['processEventPayment'] = useCallback(
        async (signUps, onError) => {
            try {
                breadcrumb(`[processEventPayment]: starting`, { signUps });
                const checkoutSecret = await fetchStripeCheckoutSecret(signUps);
                setCheckoutSecret(checkoutSecret);
                setShowModal(true);
                breadcrumb(`[processEventPayment]: Processing payment for event`, {
                    signUps,
                    checkoutSecret,
                });
            } catch (e) {
                breadcrumb(`[processEventPayment]: unhandled ${stringFromError(e)}`, {
                    signUps,
                    e,
                });
                if (e instanceof Error) logError(e);
                else log(`[processEventPayment]: ${stringFromError(e)}`);

                const msg = `Failed to process payment: ${stringFromError(e)}`;

                onError(msg);
            }
        },
        [],
    );

    const paymentContext: PaymentContextValues = useMemo(() => {
        return {
            loading: showModal,
            processEventPayment,
        };
    }, [showModal, processEventPayment]);

    console.log('[PaymentProvider]', { showModal, checkoutSecret });

    return (
        <PaymentContext.Provider value={paymentContext}>
            {
                <>
                    {children}
                    <Dialog fullWidth maxWidth="lg" open={showModal}>
                        <DialogTitle textAlign="center">
                            Almost Done, pay now to reserve your spot!
                        </DialogTitle>
                        <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    </Dialog>
                </>
            }
        </PaymentContext.Provider>
    );
}
